import {Suspense, useCallback, useEffect, useRef, useState} from 'react'

import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {PencilIcon} from '@primer/octicons-react'
import {Button, IconButton, PageHeader, useResponsiveValue} from '@primer/react'
import {useHeaderPageData} from '../page-data/loaders/use-header-page-data'
import {PullRequestBanners} from './PullRequestBanners'
import {FilesChangedFeedback} from './FilesChangedFeedback'
import {PullRequestHeaderSummary} from './PullRequestHeaderSummary'
import {PullRequestEditTitleForm} from './PullRequestEditTitleForm'
import {PullRequestStateLabel} from './PullRequestStateLabel'
import type {AliveChannels, HeaderPageData} from '../page-data/payloads/header'
import {PullRequestHeaderNavigation} from './PullRequestHeaderNavigation'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {useHeaderLiveUpdates} from '../hooks/use-header-live-updates'
import {PullRequestCodeButton} from './PullRequestCodeButton'
import {DiffStat} from './DiffStat'
import {clsx} from 'clsx'
import styles from './PullRequestHeader.module.css'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import {isLoggedIn} from '@github-ui/client-env'
import {MergeMethodContextProvider, MergeStatusButtonWrapper} from '@github-ui/mergebox'
import {PullRequestState} from '../types'
import {StickyPullRequestHeader, type StickyPullRequestHeaderProps} from './StickyPullRequestHeader'
import {setTitle} from '@github-ui/document-metadata'
import {PullRequestsTopBanners} from './banners/PullRequestsTopBanners'
import {useIsDockedPanelOpen, useDockedPanelActions} from '../contexts/DockedPanelContext'
import {MergeStatusSidePanel} from './toolbar/MergeStatusSidePanel'

export type PullRequestHeaderProps = HeaderPageData & {
  isFilesRoute?: boolean
  stickyHeaderVariant?: StickyPullRequestHeaderProps['pageVariant']
}

/**
 * LivePullRequestHeader is a thin wrapper around PullRequestHeader that will automatically update the header when the
 * alive channel is triggered with `{title_updated: true}`. This lives here instead of the base page so that
 * it doesn't re-render the entire page when the title is updated.
 */
export function LivePullRequestHeader({
  aliveChannels,
  bannersData,
  isFilesRoute,
  stickyHeaderVariant,
  pullRequest,
  pageTitle,
  repository,
  urls,
  user,
  mergeStatusButtonData,
  stack,
  viewSettings,
}: PullRequestHeaderProps & {aliveChannels: AliveChannels; isFilesRoute?: boolean}) {
  const {data: headerData} = useHeaderPageData({
    aliveChannels,
    bannersData,
    pageTitle,
    pullRequest,
    repository,
    stack,
    urls,
    user,
    mergeStatusButtonData,
    viewSettings,
  })
  useHeaderLiveUpdates(aliveChannels)

  return <PullRequestHeader {...headerData} isFilesRoute={isFilesRoute} stickyHeaderVariant={stickyHeaderVariant} />
}

export function PullRequestHeader({
  bannersData,
  isFilesRoute,
  stickyHeaderVariant,
  pullRequest,
  repository,
  pageTitle,
  urls,
  user,
  mergeStatusButtonData,
  stack,
}: PullRequestHeaderProps) {
  const [isEditing, setIsEditing] = useState(false)
  const editButtonRedesign = isFeatureEnabled('pull_request_edit_button_redesign')

  const shouldShowLoggedInOptions = isLoggedIn()
  const isPROpen = pullRequest.state !== PullRequestState.Closed && pullRequest.state !== PullRequestState.Merged
  const {channels, headSha, helpUrl, viewerLogin, defaultMergeMethod} = mergeStatusButtonData || {}
  const hasMergeStatusButton = isFeatureEnabled('pull_request_merge_status_button')
  const mergeStatusButtonDisabled = useFeatureFlag('pull_request_merge_status_button_opt_out')
  const shouldRenderMergeStatusButton =
    isPROpen &&
    hasMergeStatusButton &&
    !mergeStatusButtonDisabled &&
    channels &&
    headSha &&
    helpUrl &&
    viewerLogin &&
    defaultMergeMethod
  const headerButtonSize = hasMergeStatusButton ? 'medium' : 'small'

  const sidePanelOpen = useIsDockedPanelOpen()
  const robustSmallScreenSupport = useFeatureFlag('prx_robust_small_screen_support')

  // Docked merge status panel — only active on files/changes routes
  const isPrsDockedSidePanelEnabled = isFeatureEnabled('prs_docked_side_panel')
  const isDockedMode = !!(isFilesRoute && isPrsDockedSidePanelEnabled)
  const dockedPanelActions = useDockedPanelActions()
  const mergeStatusButtonRef = useRef<HTMLButtonElement>(null)

  const handleToggleDockedMergePanel = useCallback(() => {
    dockedPanelActions.togglePanel('mergeStatus', mergeStatusButtonRef.current)
  }, [dockedPanelActions])

  useEffect(() => {
    if (pageTitle) {
      setTitle(pageTitle)
    }
  }, [pageTitle])

  const feedbackRenderLocation = useResponsiveValue({narrow: 'mobile', regular: 'desktop'}, 'desktop') as
    | 'mobile'
    | 'desktop'

  const titleActions = () => (
    <>
      <div className="js-pull-header-details" data-pull-is-open={isPROpen ? 'true' : 'false'} hidden />
      {isFilesRoute && (
        <FilesChangedFeedback repository={repository} urls={urls} renderLocation={feedbackRenderLocation} />
      )}
      {shouldShowLoggedInOptions && (
        <div className="d-flex gap-1">
          {user.canEditTitle && !editButtonRedesign && (
            <Button onClick={() => setIsEditing(true)} size={headerButtonSize}>
              Edit
            </Button>
          )}
          {shouldRenderMergeStatusButton && (
            <MergeMethodContextProvider defaultMergeMethod={defaultMergeMethod}>
              <MergeStatusButtonWrapper
                channels={channels}
                headSha={headSha}
                helpUrl={helpUrl}
                viewerLogin={viewerLogin}
                isDockedMode={isDockedMode}
                onToggleDockedPanel={handleToggleDockedMergePanel}
                mergeStatusButtonRef={mergeStatusButtonRef}
              />
              {isDockedMode && <MergeStatusSidePanel helpUrl={helpUrl} viewerLogin={viewerLogin} channels={channels} />}
            </MergeMethodContextProvider>
          )}
          <PullRequestCodeButton
            codespacesEnabled={repository.codespacesEnabled}
            headBranch={pullRequest.headBranch}
            isEnterprise={repository.isEnterprise}
            pullRequestNumber={pullRequest.number}
            repository={repository}
            size={headerButtonSize}
          />
        </div>
      )}
    </>
  )

  return (
    <>
      <PullRequestsTopBanners bannersData={bannersData} urls={urls} />
      {isEditing && (
        <PullRequestEditTitleForm
          initialTitle={pullRequest.title}
          pullRequestNumber={pullRequest.number}
          onCloseForm={() => setIsEditing(false)}
        />
      )}
      <PageHeader
        className={clsx(
          'flex-items-center',
          sidePanelOpen && styles.sidePanelOpen,
          styles.actionsAboveTitleOnNarrow,
          robustSmallScreenSupport && styles.actionsAboveTitleOnNarrowContainer,
        )}
      >
        {isEditing && (
          // this is done so that we still have an h1 element present when editing the title
          <h1 className="sr-only">{`${pullRequest.title} - #${pullRequest.number}`}</h1>
        )}
        {!isEditing && (
          <>
            <PageHeader.TitleArea>
              <PageHeader.Title as="h1" className="lh-condensed">
                <VerifiedHTMLText className="f1 text-normal markdown-title" html={pullRequest.titleHtml} />
                <span className="d-inline-flex flex-items-center gap-1">
                  <span className="pl-2 fgColor-muted f1-light">#{pullRequest.number}</span>
                  {user.canEditTitle && editButtonRedesign && (
                    <IconButton
                      icon={PencilIcon}
                      onClick={() => setIsEditing(true)}
                      size="medium"
                      aria-label="Edit title"
                      variant="invisible"
                    />
                  )}
                </span>
              </PageHeader.Title>
            </PageHeader.TitleArea>
            <PageHeader.Actions className="flex-items-center gap-2 position-relative">
              {titleActions()}
            </PageHeader.Actions>
          </>
        )}
        <PageHeader.Description className="d-flex flex-column flex-items-start">
          <div className="d-flex flex-column flex-sm-row gap-2 width-full flex-items-start flex-justify-between">
            <PullRequestStateLabel state={pullRequest.state} />
            <div className="flex-1 overflow-hidden">
              <PullRequestHeaderSummary
                author={pullRequest.author}
                mergedTime={pullRequest.mergedTime}
                baseBranch={pullRequest.baseBranch}
                baseRepositoryDefaultBranch={repository.defaultBranch}
                baseRepositoryName={repository.name}
                baseRepositoryOwnerLogin={repository.ownerLogin}
                canChangeBase={user.canChangeBase}
                commitsCount={pullRequest.commitsCount}
                headBranch={pullRequest.headBranch}
                headRepositoryOwnerLogin={pullRequest.headRepositoryOwnerLogin}
                headRepositoryName={pullRequest.headRepositoryName}
                isInAdvisoryRepo={pullRequest.isInAdvisoryRepo}
                isEditing={isEditing}
                mergedBy={pullRequest.mergedBy}
                mergedByName={pullRequest.mergedByName}
                repositoryId={repository.id}
                pullRequestId={pullRequest.id}
                pullRequestNumber={pullRequest.number}
                setIsEditing={setIsEditing}
                state={pullRequest.state}
                stack={stack}
              />
            </div>
          </div>
          <PullRequestBanners bannersData={bannersData} pullRequest={pullRequest} repository={repository} />
        </PageHeader.Description>
        <PageHeader.Navigation className="tmp-pt-3 tmp-px-3 tmp-ml-n3 tmp-mr-n3">
          <div className={clsx(styles.diffStatesWrapper, 'float-right d-none d-md-block')}>
            <Suspense fallback={<LoadingSkeleton width="80px" />}>
              <DiffStat />
            </Suspense>
          </div>
          <div className="flex-auto">
            <PullRequestHeaderNavigation pullRequest={pullRequest} repository={repository} urls={urls} />
          </div>
        </PageHeader.Navigation>
      </PageHeader>
      {!isFilesRoute && (
        <StickyPullRequestHeader
          repository={repository}
          pullRequest={pullRequest}
          pageVariant={stickyHeaderVariant ?? 'condensed'}
        />
      )}
    </>
  )
}
